import { DEVICE } from "base/constants";

const generateTint = (rgbArr, lightnessPercent) => {
  let factor = lightnessPercent / 100;

  rgbArr = rgbArr.map((value) => {
    let newValue = value + (255 - value) * factor;
    return Math.round(newValue);
  });

  return rgbArr;
};

const generateShade = (rgbArr, darknessPercent) => {
  let factor = (100 - darknessPercent) / 100;

  rgbArr = rgbArr.map((value) => {
    return Math.round(value * factor);
  });

  return rgbArr;
};

function hexToRgb(hex) {
  let regEx = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
  let hexCode = hex.trim();
  let hexWoHash = [...(hexCode.includes("#") ? hexCode.slice(1) : hexCode)];

  if (hexWoHash.length === 3) {
    hexCode = hexWoHash.reduce(
      (acc, item) => `${acc}${item.padStart(2, item)}`,
      ""
    );
  }

  let validHex = regEx.exec(hexCode);
  if (validHex) {
    let [, r, g, b] = validHex;
    return [r, g, b].map((item) => parseInt(item, 16));
  } else {
    throw new Error("invalid hex value");
  }
}

function rgbtoHex(rgbArr) {
  return rgbArr.reduce((acc, item) => {
    let hex = item.toString(16);
    let finalHex = hex.length === 1 ? hex.padStart(2, "0") : hex;
    return `${acc}${finalHex}`;
  }, "#");
}

// brightness of the color
function findLuminance(rgbArr) {
  // find linear rgb value
  let [lr, lg, lb] = rgbArr.map((value) => {
    value = value / 255;
    if (value <= 0.04045) {
      return value / 12.92;
    } else {
      return Math.pow((value + 0.055) / 1.055, 2.4);
    }
  });

  // 0 to 1 scale
  return lr * 0.2126 + lg * 0.7152 + lb * 0.0722;
}

function generateLightModeColors(rgbArr, kind) {
  let colors = { ...generateGrayPallet(rgbArr) };

  if (kind === DEVICE.PWA) {
    colors = {
      ...colors,
      500: generateTint(rgbArr, 0),
      300: generateTint(rgbArr, 50), //lightest
      100: generateTint(rgbArr, 90), //light
      dark: generateShade(rgbArr, 40)
    };
  } else {
    colors = {
      ...colors,
      100: generateTint(rgbArr, 93),
      200: generateTint(rgbArr, 87),
      300: generateTint(rgbArr, 67),
      400: generateTint(rgbArr, 40),
      500: generateTint(rgbArr, 0),
      600: generateShade(rgbArr, 39),
      700: generateShade(rgbArr, 66),
      800: generateShade(rgbArr, 93)
    };
  }

  for (let color in colors) {
    colors[color] = rgbtoHex(colors[color]);
  }

  return colors;
}

function generateDarkModeColors(rgbArr, kind) {
  let darkModeRgb = generateTint(rgbArr, 40);

  let colors = {};

  if (kind === DEVICE.PWA) {
    // same color variables for pwa dark and light mode
    colors = {
      500: generateTint(rgbArr, 0),
      300: generateTint(rgbArr, 50), //lightest
      100: generateTint(rgbArr, 90), //light
      dark: generateShade(rgbArr, 40)
    };
  } else {
    colors = {
      100: generateShade(darkModeRgb, 85),
      200: generateShade(darkModeRgb, 79),
      300: generateShade(darkModeRgb, 63),
      400: generateShade(darkModeRgb, 30),
      500: generateShade(darkModeRgb, 0),
      600: generateTint(rgbArr, 67),
      700: generateTint(rgbArr, 80),
      800: generateTint(rgbArr, 93)
    };
  }
  for (let color in colors) {
    colors[color] = rgbtoHex(colors[color]);
  }

  return colors;
}

function generateGrayPallet(rgbArr, type) {
  let tintGrayList = generateTint(rgbArr, 80);

  return {
    page: generateTint(rgbArr, 94),
    gray100: generateTint(rgbArr, 90),
    gray200: generateTint(rgbArr, 80),
    gray300: generateTint(rgbArr, 70),
    gray400: generateShade(tintGrayList, 25),
    gray500: generateShade(tintGrayList, 45),
    gray600: generateShade(tintGrayList, 80)
  };
}

export function generateColors(hex = "#000", kind = DEVICE.WEB) {
  let rgbArr = hexToRgb(hex);

  let colorPallet = {
    lightPallet: generateLightModeColors(rgbArr, kind),
    darkPallet: generateDarkModeColors(rgbArr, kind)
  };

  let luminance = findLuminance(rgbArr);

  return {
    colorPallet,
    isContrast: luminance >= 0.5
  };
}

export function isHex(hex) {
  let regEx = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

  let hexWoHash = [...(hex.includes("#") ? hex.slice(1) : hex)];
  if (hexWoHash.length === 3) {
    hex = hexWoHash.map((i) => i + i).join("");
  }
  if (regEx.exec(hex) !== null) {
    return true;
  }
  return false;
}

const genrateVariablesStr = (colorObj = {}, kind = DEVICE.WEB) => {
  let colors = "";
  let grays = "";

  for (let color in colorObj) {
    if (color.includes("page")) {
      grays = `${grays}--kf-color-bg-white:${colorObj[color]};
    --kf-color-bg-gray:${colorObj[color]};
    `;
    } else if (color.includes("gray")) {
      grays = `${grays}--${"gray"}-${color.slice(-3)}:${colorObj[color]};`;
    } else {
      colors = `${colors}--${
        kind === DEVICE.PWA ? "" : "kf-color-primary"
      }-${color}:${colorObj[color]};`;
    }
  }
  return { colors, grays };
};

const createPWAvariablesStr = (colorObj = {}) => {
  let { lightPallet, darkPallet } = colorObj;
  let kind = DEVICE.PWA;

  let LightVars = genrateVariablesStr(lightPallet, kind);
  let DarkVars = genrateVariablesStr(darkPallet, kind);

  let light = `--kf-color-primary-alpha-50:${lightPallet["500"]}80;\n
  --kf-color-primary-alpha-15:${lightPallet["500"]}26;\n
  ${LightVars.colors}`;

  let grayLight = `--gray-200-dull-in-dark:${lightPallet.gray200};\n
  --gray-300-outline-bright-in-dark:${lightPallet.gray300};\n
  ${LightVars.grays}`;

  let dark = `--kf-color-primary-alpha-50:${darkPallet["500"]}80;\n
  --kf-color-primary-alpha-15:${darkPallet["500"]}26;\n
  ${DarkVars.colors}`;

  return { light, grayLight, dark };
};

const createWEBvariablesStr = (colorObj = {}, isContrast = false) => {
  let { lightPallet, darkPallet } = colorObj;
  let kind = DEVICE.WEB;

  let LightVars = genrateVariablesStr(lightPallet, kind);
  let DarkVars = genrateVariablesStr(darkPallet, kind);

  let light = `--text-blue:var(--kf-color-primary-${isContrast ? 600 : 500});\n
  --kf-color-white:${isContrast ? "#282d36" : "#ffffff"};\n
  ${LightVars.colors}`;

  let grayLight = `--kf-color-gray-alpha-25:${lightPallet.gray400}40;\n
  --kf-color-gray-alpha-50:${lightPallet.gray400}80;\n
  --kf-color-gray-alpha-75:${lightPallet.gray400}bf;\n
  --kf-color-gray-200-dark:${lightPallet.gray300}80;\n
  ${LightVars.grays}`;

  let dark = `--text-blue:var(--kf-color-primary-500);\n
    --kf-color-white:${isContrast ? "#ffffff" : "#282d36"};\n
    ${DarkVars.colors}`;

  return { light, grayLight, dark };
};

export const getColorVariables = (
  color,
  changeBg = false,
  kind = DEVICE.WEB
) => {
  let { colorPallet, isContrast } = generateColors(color, kind);

  var { light, grayLight, dark } =
    kind === DEVICE.PWA
      ? createPWAvariablesStr(colorPallet)
      : createWEBvariablesStr(colorPallet, isContrast);

  let cssTextContent = `body{${light}${
    changeBg ? grayLight : ""
  }}\nbody.dark,body.dim{${dark}}`;

  return [cssTextContent, isContrast];
};
